// Report-param grammar for a correlation-finder partial -- the chart and the table drawn from the
// pair of patterns the researcher crossed.
//
// Two params carry it, addressed by prefix:
//   cm   the mode the chart is drawn in
//   cf   how the pair is laid out: the grouping, and which axis the values are counted on
//
// `cf` is an object param rather than a scalar: the two settings travel together and are dropped
// together, once both are back at their default.
//
// The grammar was split across two coffee views -- the block read `cf`, the chart view read `cm`
// and wrote both. It is one bag here.

import {
  getAvailableModes,
  getDefaultChartMode,
  isAboveLimit
} from '@aytm/app/stats/page/question-views/cq/components/partial/lib/chartModes'
import { encodeZoomParam } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'

export const DEFAULT_GROUP_TYPE = 'grouped'
export const DEFAULT_AXIS_TYPE = 'percents'

const OVER_LIMIT_CHART_MODE = 'bcolumns'

export const resolveGroupType = ({ cf } = {}, format = {}) => cf?.groupType || format?.groupType || DEFAULT_GROUP_TYPE

// An answer whose chart columns carry no percents has nothing to count them from, so the axis is
// forced to respondents whatever was stored. An answer that names no chart columns at all says
// nothing either way, and is left to what was stored -- the way the coffee's numeric comparison
// against a missing index left it.
export const resolveAxisType = ({ cf } = {}, format = {}, headers = {}) => {
  if (headers?.chart && !headers.chart.includes('percents')) return 'respondents'

  return cf?.axisType || format?.axisType || DEFAULT_AXIS_TYPE
}

// The mode the chart opens on. A stored mode the current pair cannot be drawn in is dropped, and a
// pair too big to draw as grouped columns opens on bars outright -- it does not get to fall back to
// the report-wide mode the way a pair within the limit does.
export const resolveChartMode = (
  { cm } = {},
  { batches, ignorePercents, groupType, questionChartMode, globalChartMode } = {}
) => {
  const available = getAvailableModes({ batches, ignorePercents, groupType })
  if (available.includes(cm)) return cm

  if (isAboveLimit(batches, groupType, ignorePercents)) return OVER_LIMIT_CHART_MODE

  return getDefaultChartMode({ questionChartMode, globalChartMode, batches, ignorePercents, groupType })
}

// A setting that says what the partial would have shown anyway is not worth storing -- a zoom
// included, which is silent while it matches the one the whole report is read at.
export const calcParamsSection = ({
  chartMode,
  groupType,
  axisType,
  zoomLevel,
  batches,
  ignorePercents,
  questionChartMode,
  globalChartMode,
  globalZoomLevel
} = {}) => {
  const defaultChartMode = getDefaultChartMode({
    questionChartMode,
    globalChartMode,
    batches,
    ignorePercents,
    groupType
  })

  const atDefaultLayout = groupType === DEFAULT_GROUP_TYPE && axisType === DEFAULT_AXIS_TYPE

  return {
    cf: atDefaultLayout ? null : { groupType, axisType },
    cm: chartMode === defaultChartMode ? null : chartMode,
    z: encodeZoomParam(zoomLevel, globalZoomLevel)
  }
}
